import createClient from "openapi-fetch";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { paths as BooksPaths, paths as PlatformPaths } from "@dolfin/api-admin";
import type { OrganisationRole } from "./types";

/** Where the books link out to: the host app's own pages. */
export interface BooksLinks {
  invoice: (id: string) => string;
  bill: (id: string) => string;
  expense: (id: string) => string;
  creditNote: (id: string) => string;
  supplierCreditNote: (id: string) => string;
  accountingConnection: string;
  bankConnections: string;
}

interface BooksContextValue {
  /** Books routes sit in the admin document until DOL-23 (D19). */
  books: ReturnType<typeof createClient<BooksPaths>>;
  /** Chart of accounts, tax rates, bank and accounting connections. */
  platform: ReturnType<typeof createClient<PlatformPaths>>;
  role: OrganisationRole | null;
  /** Owner/Admin: setup, activate, close and the close date (D22). */
  canManage: boolean;
  links: BooksLinks;
  /** Bumped by the host when the books may have changed elsewhere (an agent turn); every books read re-runs. */
  changes: number;
  /** False where the host forbids closing books (a demo organisation), whatever the role. */
  canClose: boolean;
  /** Opens the host's agent with a question drafted for the reader to send; absent where the host has no agent. */
  askAgent?: (prompt: string) => void;
}

const BooksContext = createContext<BooksContextValue | null>(null);

/**
 * The books' only boundary with the host app (D20): it hands in an authenticated fetch, the caller's role and its
 * links, and nothing under books/ imports the host's pages, so DOL-24 can lift the folder into a package.
 */
export function BooksProvider({
  baseUrl,
  authFetch,
  role,
  links,
  changes = 0,
  canClose = true,
  askAgent,
  children,
}: {
  baseUrl: string;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  role: OrganisationRole | null;
  links: BooksLinks;
  changes?: number;
  canClose?: boolean;
  askAgent?: (prompt: string) => void;
  children: ReactNode;
}) {
  const clients = useMemo(() => {
    // Body read as text: re-wrapping a Request's stream body needs `duplex`, and every books body is JSON.
    const fetch = async (r: Request) =>
      authFetch(r.url, {
        method: r.method,
        headers: r.headers,
        body: r.method === "GET" || r.method === "HEAD" ? undefined : await r.text(),
        signal: r.signal,
      });
    return {
      books: createClient<BooksPaths>({ baseUrl, fetch }),
      platform: createClient<PlatformPaths>({ baseUrl, fetch }),
    };
  }, [baseUrl, authFetch]);
  const value = useMemo<BooksContextValue>(
    () => ({ ...clients, role, canManage: role === "Owner" || role === "Admin", links, changes, canClose, askAgent }),
    [clients, role, links, changes, canClose, askAgent],
  );
  return <BooksContext.Provider value={value}>{children}</BooksContext.Provider>;
}

export function useBooks(): BooksContextValue {
  const ctx = useContext(BooksContext);
  if (!ctx) throw new Error("useBooks must be used inside <BooksProvider>");
  return ctx;
}
